import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Users, ShieldCheck, AlertTriangle, XCircle, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard, Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const date = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

function planState(r) {
  const s = r.subscription;
  if (!s) return ["Not subscribed", "gray"];
  if (r.in_grace) return ["Grace period", "amber"];
  if (r.active && (s.cancelled_at || s.status === "cancelled")) return ["Active · cancelling", "amber"];
  if (r.active) return ["Active", "green"];
  if (["halted", "pending"].includes(s.status)) return ["Payment failed", "crimson"];
  if (s.status === "created") return ["Mandate not set up", "gray"];
  return [s.status === "cancelled" ? "Cancelled" : "Inactive", "crimson"];
}

const FILTERS = [["all", "All"], ["active", "Active"], ["grace", "Grace"], ["failed", "Failed payments"], ["none", "Not subscribed"]];
const match = (r, f) => ({
  all: true, active: r.active && !r.in_grace, grace: r.in_grace,
  failed: (r.subscription?.failures?.length || 0) > 0 || ["halted", "pending"].includes(r.subscription?.status),
  none: !r.subscription,
}[f]);

function Failures({ list }) {
  if (!list?.length) return <span className="text-muted-foreground">—</span>;
  const last = list[list.length - 1];
  return (
    <div title={list.map((f) => `${date(f.at)}: ${f.reason}`).join("\n")}>
      <span className="font-semibold text-crimson">{list.length}</span>
      <div className="text-xs text-muted-foreground truncate max-w-[180px]">{date(last.at)} · {last.reason}</div>
    </div>
  );
}

export default function AdminEducatorPlans() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const load = () => api.get("/educator/subscription/admin/list").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const refresh = async (sid) => {
    try { await api.post(`/educator/subscription/admin/${sid}/refresh`); toast.success("Synced with Razorpay"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const shown = useMemo(() => (rows || []).filter((r) => match(r, filter) && `${r.educator} ${r.email}`.toLowerCase().includes(q.toLowerCase())), [rows, filter, q]);
  const all = rows || [];

  return (
    <div data-testid="admin-educator-plans">
      <PageHeader title="Educator Plans" subtitle="Monthly ₹999 + GST auto-debit status for every educator" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Educators" value={all.length} icon={Users} testid="ep-stat-total" />
        <StatCard label="Active plans" value={all.filter((r) => r.active).length} icon={ShieldCheck} accent="bg-emerald-600" testid="ep-stat-active" />
        <StatCard label="In grace period" value={all.filter((r) => r.in_grace).length} icon={AlertTriangle} accent="bg-amber" testid="ep-stat-grace" />
        <StatCard label="With failed payments" value={all.filter((r) => match(r, "failed")).length} icon={XCircle} accent="bg-crimson" testid="ep-stat-failed" />
      </div>
      <div className="flex flex-wrap gap-2 items-center mb-4">
        {FILTERS.map(([k, l]) => <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} className={filter === k ? "viriks-navy text-white" : ""} onClick={() => setFilter(k)} data-testid={`ep-filter-${k}`}>{l}</Button>)}
        <div className="relative ml-auto w-full sm:w-64"><Search size={15} className="absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ep-search" /></div>
      </div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground"><tr>{["Educator", "Plan status", "Paid until", "Next debit", "Payments", "Failed payments", "Live courses", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {rows && shown.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="ep-empty">No educators match this view.</td></tr>}
            {shown.map((r) => {
              const s = r.subscription;
              const [label, tone] = planState(r);
              const nextDebit = s && r.active && !s.cancelled_at && s.status !== "cancelled" ? (s.charge_at || s.current_end) : null;
              return (
                <tr key={r.educator_id} className="border-t border-border" data-testid={`ep-row-${r.educator_id}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-navy">{r.educator}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
                  <td className="px-4 py-3"><Badge tone={tone}>{label}</Badge>{s?.status && <div className="text-xs text-muted-foreground mt-1">Razorpay: {s.status}</div>}</td>
                  <td className="px-4 py-3">{date(s?.paid_until)}</td>
                  <td className="px-4 py-3 font-medium" data-testid={`ep-next-${r.educator_id}`}>{date(nextDebit)}</td>
                  <td className="px-4 py-3">{s?.paid_count || 0}</td>
                  <td className="px-4 py-3" data-testid={`ep-failed-${r.educator_id}`}><Failures list={s?.failures} /></td>
                  <td className="px-4 py-3">{r.published_courses}{!r.active && r.published_courses > 0 && <div className="text-xs text-crimson">not on sale</div>}</td>
                  <td className="px-4 py-3">{s && <Button size="sm" variant="ghost" title="Sync with Razorpay" onClick={() => refresh(s.id)} data-testid={`ep-refresh-${r.educator_id}`}><RefreshCw size={14} /></Button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
